import { create } from "zustand";
import { persist } from "zustand/middleware";

import { IErrorStore } from "./types";

const updateStateForError = <FieldKey extends string, FlowSlug extends string>(
  state: IErrorStore<FieldKey, FlowSlug>,
  key: FieldKey,
  slug: FlowSlug,
  message: string,
) => {
  const newValidated = { ...state.validatedFields };
  if (newValidated[slug]) {
    newValidated[slug] = newValidated[slug]!.filter((k) => k !== key);
  }

  return {
    errors: {
      ...state.errors,
      [key]: message,
    },
    validatedFields: newValidated,
  };
};

const updateStateForClearError = <FieldKey extends string, FlowSlug extends string>(
  state: IErrorStore<FieldKey, FlowSlug>,
  key: FieldKey,
  slug: FlowSlug,
) => {
  const newErrors = { ...state.errors };
  delete newErrors[key];

  const newValidated = { ...state.validatedFields };
  const current = new Set(newValidated[slug] || []);
  current.add(key);
  newValidated[slug] = Array.from(current);

  return {
    errors: newErrors,
    validatedFields: newValidated,
  };
};

export const createErrorStore = <
  FieldKey extends string,
  FlowSlug extends string,
>(
  storeName: string,
) => create<IErrorStore<FieldKey, FlowSlug>>()(
  persist(
    (set, get) => ({
      errors: {},
      validatedFields: {},

      setError: (key, slug, message) => {
        set((state) => updateStateForError(state, key, slug, message));
      },

      clearError: (key, slug) => {
        set((state) => updateStateForClearError(state, key, slug));
      },

      getError: (key) => get().errors[key],

      clearAll: () => {
        set({
          errors: {},
          validatedFields: {},
        });
      },
    }),
    { name: storeName },
  ),
);
